import { useEffect, useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../lib/api';
import { useSearchParams } from 'react-router-dom';
import type { Conversation, Message } from '../types';

export function MessagesPage() {
  const { currentUser } = useApp();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchParams] = useSearchParams();
  const [recipient,setRecipient] = useState(searchParams.get('to') || '');
  const [newMsg, setNewMsg] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const loadConversations = async () => {
    try {
      const data = await api.conversations();
      setConversations(data.conversations || []);
    } catch (e:any) {
      setError(e.message);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (convId: string) => {
    try {
      const data = await api.messages(convId);
      setMessages(data.messages || []);
    } catch(e:any) {
      setError(e.message); setMessages([]);
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (!selectedId) {setMessages([]);return;}
    loadMessages(selectedId);
    const timer=setInterval(()=>{loadMessages(selectedId);loadConversations();},5000);
    return ()=>clearInterval(timer);
  }, [selectedId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMsg.trim() || !selectedId) return;
    const body = newMsg.trim();
    setNewMsg('');
    // Optimistic: add message immediately
    const tempId = `temp-${Date.now()}`;
    setMessages((prev) => [...prev, {
      id: tempId,
      conversationId: selectedId,
      senderId: currentUser?.id ?? '',
      senderUsername: currentUser?.username ?? 'You',
      body,
      createdAt: new Date().toISOString(),
      read: true,
    }]);
    try {
      await api.sendMessage(selectedId, body);
      await loadMessages(selectedId);
      await loadConversations();
    } catch (e: any) {
      setNewMsg(body); setError(e?.message || 'Failed to send message');
      // Remove the optimistic message on failure
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    }
  };

  const startNewConversation = async (username: string) => {
    if (!username.trim()) return;
    try {
      const data = await api.startConversation(username.trim());
      if (data.conversation) {
        setConversations((prev) => {
          if (prev.find((c) => c.id === data.conversation.id)) return prev;
          return [data.conversation, ...prev];
        });
        setSelectedId(data.conversation.id);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not start conversation');
    }
  };

  const blockUser = async (conversationId: string) => {
    try {
      await api.blockUser(conversationId);
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (selectedId === conversationId) {
        setSelectedId(null);
        setMessages([]);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not block user');
    }
  };

  const selectedConv = conversations.find((c) => c.id === selectedId);

  return (
    <div className="grid messages-layout" style={{ gridTemplateColumns: '320px minmax(0,1fr)', minHeight: 'calc(100vh - 200px)' }}>
      {/* Conversation list */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="section-title" style={{ margin: 0, padding: '16px 16px 8px' }}>
          Messages
        </div>
        <div style={{ padding: '0 16px 12px' }}>
          <input
            className="input btn-sm"
            placeholder="Artist username (press Enter)"
            aria-label="Artist username"
            value={recipient}
            onChange={e=>setRecipient(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter')startNewConversation(recipient);}}

          />
          <button className="btn btn-outline btn-sm" disabled={!recipient.trim()} onClick={()=>startNewConversation(recipient)}>Start conversation</button>
        </div>
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {loading ? (
            <div className="muted" style={{ padding: 16, fontSize: 14 }}>Loading conversations...</div>
          ) : conversations.length === 0 ? (
            <div className="muted" style={{ padding: 16, fontSize: 14 }}>
              No conversations yet. Search for a username above to start a new chat with another artist or admin.
            </div>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                className={`nav-link${selectedId === c.id ? ' active' : ''}`}
                style={{ width: '100%', textAlign: 'left', border: 'none', borderRadius: 0 }}
                onClick={() => setSelectedId(c.id)}
              >
                <span className="nav-ico" style={{ background: c.participantAvatarColor, width: 28, height: 28, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                  {c.participantUsername.slice(0, 2).toUpperCase()}
                </span>
                <span style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontWeight: selectedId === c.id ? 700 : 500 }}>{c.participantUsername}</div>
                  <div className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.lastMessage}</div>
                </span>
                {c.unreadCount > 0 && (
                  <span className="badge-count" style={{ marginLeft: 8 }}>{c.unreadCount}</span>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat panel */}
      <div className="card" style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
        {!selectedConv ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: 40 }}>
            <div className="muted" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>💬</div>
              <p>Select a conversation or search for a username to start chatting.</p>
              <p style={{ fontSize: 13, marginTop: 8 }}>Artists can collaborate, share ideas, and connect with admin directly.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-between" style={{ padding: '12px 16px', borderBottom: '1px solid var(--iam-divider)' }}>
              <div className="flex gap-8" style={{ alignItems: 'center' }}>
                <div style={{ background: selectedConv.participantAvatarColor, width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>
                  {selectedConv.participantUsername.slice(0, 2).toUpperCase()}
                </div>
                <strong>{selectedConv.participantUsername}</strong>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => blockUser(selectedConv.id)}>
                Block user
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {messages.length === 0 ? (
                <div className="muted" style={{ textAlign: 'center', padding: 20, fontSize: 14 }}>
                  No messages yet. Say hello.
                </div>
              ) : (
                messages.map((m) => {
                  const isMe = m.senderId === currentUser?.id || m.senderUsername === currentUser?.username;
                  return (
                    <div key={m.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '70%' }}>
                      <div
                        className={isMe ? 'chip chip-approved' : 'chip chip-requested'}
                        style={{
                          padding: '8px 14px',
                          borderRadius: 12,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          display: 'block',
                        }}
                      >
                        {m.body}
                      </div>
                      <div className="muted" style={{ fontSize: 11, marginTop: 2, textAlign: isMe ? 'right' : 'left' }}>
                        {isMe ? 'You' : m.senderUsername} · {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div style={{ padding: 12, borderTop: '1px solid var(--iam-divider)', display: 'flex', gap: 8 }}>
              <input
                className="input"
                style={{ flex: 1 }}
                placeholder="Type your message..."
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
              />
              <button className="btn btn-primary" onClick={sendMessage} disabled={!newMsg.trim()}>
                Send
              </button>
            </div>
          </>
        )}
      </div>

      {error && <div className="field-hint" style={{ color: 'var(--iam-danger)', gridColumn: '1 / -1' }}>{error}</div>}

      <div className="field-hint" style={{ gridColumn: '1 / -1' }}>
        <strong> Messaging Rules:</strong> No harassment, threats, hate speech, spam, or sharing personal info.
        Block and report any user who violates these rules. All messages are logged for safety.
      </div>
    </div>
  );
}
